import { WPCF7FormField } from '@/types'

export type ConditionalLogicRule = {
  fieldId: string
  operator: string
  value: string
}

export type ConditionalLogic = {
  actionType: 'show' | 'hide'
  logicType: 'all' | 'any'
  rules: ConditionalLogicRule[]
}

/**
 * Parse conditionalLogic from field (can be JSON string or object)
 */
export const parseConditionalLogic = (
  conditionalLogic: string | ConditionalLogic | null | undefined
): ConditionalLogic | null => {
  if (!conditionalLogic) return null

  if (typeof conditionalLogic === 'string') {
    try {
      return JSON.parse(conditionalLogic)
    } catch {
      return null
    }
  }

  return conditionalLogic as ConditionalLogic
}

/**
 * Evaluate a single rule against form values
 */
const evaluateRule = (rule: ConditionalLogicRule, formValues: Record<string, any>): boolean => {
  const fieldValue = formValues[`input_${rule.fieldId}`]
  const ruleValue = rule.value

  // Check if the source field has a meaningful value
  const hasValue =
    fieldValue !== undefined &&
    fieldValue !== null &&
    fieldValue !== '' &&
    !(Array.isArray(fieldValue) && fieldValue.length === 0)

  // Handle array values (multi_choice, checkbox groups)
  const normalizedFieldValue = Array.isArray(fieldValue) ? fieldValue : [fieldValue]

  switch (rule.operator) {
    case 'is':
      // If source field has no value, condition is not met
      if (!hasValue) return false
      if (Array.isArray(fieldValue)) {
        return fieldValue.includes(ruleValue)
      }
      return String(fieldValue || '') === String(ruleValue)

    case 'isnot':
      // If source field has no value, condition is not met (we can't say it's "not" something if nothing is selected)
      if (!hasValue) return false
      if (Array.isArray(fieldValue)) {
        return !fieldValue.includes(ruleValue)
      }
      return String(fieldValue || '') !== String(ruleValue)

    case '>':
      if (!hasValue) return false
      return Number(fieldValue) > Number(ruleValue)

    case '<':
      if (!hasValue) return false
      return Number(fieldValue) < Number(ruleValue)

    case '>=':
      if (!hasValue) return false
      return Number(fieldValue) >= Number(ruleValue)

    case '<=':
      if (!hasValue) return false
      return Number(fieldValue) <= Number(ruleValue)

    case 'contains':
      if (!hasValue) return false
      return normalizedFieldValue.some((v) =>
        String(v || '')
          .toLowerCase()
          .includes(String(ruleValue).toLowerCase())
      )

    case 'starts_with':
      if (!hasValue) return false
      return String(fieldValue || '')
        .toLowerCase()
        .startsWith(String(ruleValue).toLowerCase())

    case 'ends_with':
      if (!hasValue) return false
      return String(fieldValue || '')
        .toLowerCase()
        .endsWith(String(ruleValue).toLowerCase())

    case 'isempty':
      return !hasValue

    case 'isnotempty':
      return hasValue

    default:
      return false
  }
}

/**
 * Evaluate if a field should be visible based on its conditional logic and form values
 */
export const isFieldVisible = (field: WPCF7FormField, formValues: Record<string, any>): boolean => {
  const logic = parseConditionalLogic(field.conditionalLogic)

  // No conditional logic = always visible
  if (!logic || !logic.rules || logic.rules.length === 0) {
    return true
  }

  const results = logic.rules.map((rule) => evaluateRule(rule, formValues))

  // Determine if conditions are met based on logicType
  const conditionsMet = logic.logicType === 'all' ? results.every(Boolean) : results.some(Boolean)

  // Apply actionType
  if (logic.actionType === 'show') {
    return conditionsMet
  } else {
    // actionType === 'hide'
    return !conditionsMet
  }
}

/**
 * Get all visible field IDs based on current form values
 */
export const getVisibleFieldIds = (
  fields: WPCF7FormField[],
  formValues: Record<string, any>
): Set<string> => {
  const visibleIds = new Set<string>()

  fields.forEach((field) => {
    if (isFieldVisible(field, formValues)) {
      visibleIds.add(field.id)
    }
  })

  return visibleIds
}
